import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { ElementTypes, SelectOption } from '@fcom/common/interfaces';
import {
  isBlank,
  isEmpty,
  isFrequentFlyerNumber,
  PHONE_PATTERN,
  PHONE_PREFIX_PATTERN,
  sortBy,
  uniqueBy,
} from '@fcom/utils';
import { LanguageService } from '@fcom/ui-translate';
import { ControlData, FinnairFrequentFlyerCardItem, FinnairPassengerCode } from '@fcom/dapi/api/models';
import { ValidationErrors } from '@fcom/common/utils';
import { TypedFormGroup } from '@fcom/service-forms';
import { finShare } from '@fcom/rx';
import { ButtonMode, ButtonSize, ButtonTheme, ButtonType, CheckBoxTheme, IconPosition } from '@fcom/ui-components';
import { AppConstants } from '@fcom/booking/app-constants';
import { ProfileType } from '@fcom/core-api/login';
import { isFrequentFlyerFieldFilled } from '@fcom/booking/utils/passenger-utils';

import { GENDER_OPTIONS } from '../../interfaces';

export const PaxFormFields = {
  gender: 'gender',
  firstName: 'firstName',
  lastName: 'lastName',
  type: 'type',
  email: 'email',
  phone: 'phone',
  phones: 'phones',
  phoneNumber: 'phoneNumber',
  phonePrefix: 'phonePrefix',
  birthDate: 'birthDate',
  yearOfBirth: 'yearOfBirth',
  monthOfBirth: 'monthOfBirth',
  dateOfBirth: 'dateOfBirth',
  frequentFlyerCard: 'frequentFlyerCard',
  frequentFlyerCards: 'frequentFlyerCards',
  companyCode: 'companyCode',
  cardNumber: 'cardNumber',
  joinFinnairPlus: 'joinFinnairPlus',
};

export interface TranslatedCountryCode {
  countryCode: string;
  name: string;
  phonePrefix: string;
}

const SEPARATOR: SelectOption = {
  name: '-------------------',
  value: '-------------------',
  disabled: true,
};

const countryCodeToOption = (cc: TranslatedCountryCode): SelectOption =>
  cc && {
    name: `${cc.name} (+${cc.phonePrefix})`,
    value: `${cc.countryCode}|${cc.phonePrefix}`,
  };

const matchingCountryCode =
  (countryCode = '') =>
  (cc: TranslatedCountryCode) =>
    countryCode.toLowerCase() === (cc.countryCode || '').toLowerCase();

export const validateFrequentFlyer = (
  formGroup: UntypedFormGroup,
  existingCards: FinnairFrequentFlyerCardItem[]
): ValidationErrors => {
  const companyCode = formGroup.get(PaxFormFields.companyCode);
  const cardNumber = formGroup.get(PaxFormFields.cardNumber);

  if (!companyCode.value && !cardNumber.value) {
    companyCode.setErrors(null);
    cardNumber.setErrors(null);
    return null;
  }

  if (!isFrequentFlyerNumber(companyCode.value, cardNumber.value) && !!companyCode.value) {
    cardNumber.setErrors({
      custom: 'notValidCardNumber',
    });
  } else if (
    existingCards?.some((card) => card.cardNumber === cardNumber.value && card.companyCode === companyCode.value)
  ) {
    cardNumber.setErrors({
      custom: 'sameCardNumberExists',
    });
  } else {
    cardNumber.setErrors(null);
  }

  companyCode.setErrors(Validators.required(companyCode));
  return null;
};

export const validatePhoneNumber = (
  formGroup: UntypedFormGroup,
  phoneNumberRegex: string,
  required: boolean
): ValidationErrors => {
  const phonePrefix = formGroup.get(PaxFormFields.phonePrefix);
  const phoneNumber = formGroup.get(PaxFormFields.phoneNumber);

  if (!phonePrefix.value && !phoneNumber.value && !required) {
    phonePrefix.setErrors(null);
    phoneNumber.setErrors(null);
    return null;
  }

  const phonePrefixValidation = Validators.compose([Validators.required, Validators.pattern(PHONE_PREFIX_PATTERN)])(
    phonePrefix
  );
  const phoneNumberValidation = Validators.compose([
    Validators.required,
    Validators.pattern(phoneNumberRegex || PHONE_PATTERN),
  ])(phoneNumber);

  phonePrefix.setErrors(phonePrefixValidation);
  phoneNumber.setErrors(phoneNumberValidation);
  return null;
};

@Component({
  selector: 'fin-pax-details-form-group',
  templateUrl: 'pax-details-form-group.component.html',
  styleUrls: ['pax-details-form-group.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class PaxDetailsFormGroupComponent implements OnInit {
  @Input() passengerFormGroup: TypedFormGroup<UntypedFormGroup | UntypedFormControl>;
  @Input() groupIndex: number;
  @Input() allowMultipleFFCards = false;
  @Input() trackingContext: string;

  @Input() controlData: ControlData;
  @Input() isMainPassenger = false;
  @Input() frequentFlyerAirlines$: Observable<SelectOption[]>;
  @Input() availableDates: { days$: Observable<number[]>; months: number[]; years: number[] };
  @Input() allFrequentFlyerCards: FinnairFrequentFlyerCardItem[];
  @Input() profileType$: Observable<ProfileType>;

  @Output() focusEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() blurEvent: EventEmitter<void> = new EventEmitter<void>();

  public readonly FinnairPassengerCode = FinnairPassengerCode;
  public readonly ElementTypes = ElementTypes;

  public readonly GENDER_OPTIONS = GENDER_OPTIONS;
  public readonly FIELDS = PaxFormFields;
  public readonly REQUIRED = Validators.required;
  public readonly IconPosition = IconPosition;
  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonType = ButtonType;
  public readonly ButtonMode = ButtonMode;
  public readonly ButtonSize = ButtonSize;
  public readonly CheckBoxTheme = CheckBoxTheme;
  public readonly C = AppConstants;

  childForm: UntypedFormGroup;
  phonePrefixes$: Observable<SelectOption[]>;
  frequentFlyerCardOptions$: Observable<SelectOption[][]>;
  frequentFlyerGroupConfig = {
    [PaxFormFields.companyCode]: ['', []],
    [PaxFormFields.cardNumber]: ['', []],
  };
  phoneNumbersGroupConfig = {
    [PaxFormFields.phoneNumber]: ['', []],
    [PaxFormFields.phonePrefix]: ['', []],
  };

  frequentFlyerGroupOptions = {
    validators: (formGroup: UntypedFormGroup): ValidationErrors =>
      validateFrequentFlyer(formGroup, this.allFrequentFlyerCards),
  };
  phoneGroupOptions = {
    validators: (formGroup: UntypedFormGroup): ValidationErrors =>
      validatePhoneNumber(formGroup, this.controlData?.phoneNumberRegex, this.isMainPassenger),
  };

  dayOptions$: Observable<SelectOption[]>;
  monthOptions: SelectOption[];
  yearOptions: SelectOption[];
  showContactDetails = false;
  frequentFlyerToggle$ = new BehaviorSubject<boolean>(false);
  frequentFlyerIsOpen$: Observable<boolean> = of(false);

  constructor(
    private parentF: FormGroupDirective,
    private languageService: LanguageService,
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // availableDates is not set for non-american routes as DoB is not required.
    this.dayOptions$ = this.availableDates?.days$.pipe(
      map((days) => {
        return days.map((day) => {
          return { name: String(day), value: String(day) };
        });
      })
    );

    this.monthOptions = this.availableDates?.months.map((month) => {
      return { name: String(month), value: String(month) };
    });

    this.yearOptions = this.availableDates?.years.map((year) => {
      return { name: String(year), value: String(year) };
    });

    const phonePrefixSelected = this.passengerFormGroup.controls[PaxFormFields.phone]?.value[PaxFormFields.phonePrefix];
    const phoneNumberFilled = this.passengerFormGroup.controls[PaxFormFields.phone]?.value[PaxFormFields.phoneNumber];
    const emailFilled = this.passengerFormGroup.controls[PaxFormFields.email]?.value;

    if (emailFilled || phoneNumberFilled || phonePrefixSelected) {
      this.showContactDetails = true;
    }

    this.childForm = this.parentF.form;
    this.childForm.addControl(
      this.groupIndex.toString(),
      new UntypedFormGroup({
        ProjectName: new UntypedFormControl(''),
      })
    );

    this.setupPhonePrefixes();
    this.setupJoinFinnairPlus();

    this.passengerFormGroup.controls[PaxFormFields.frequentFlyerCard]?.addValidators((formGroup: UntypedFormGroup) =>
      validateFrequentFlyer(formGroup, this.allFrequentFlyerCards)
    );
    this.passengerFormGroup.controls[PaxFormFields.phone]?.addValidators((formGroup: UntypedFormGroup) =>
      validatePhoneNumber(formGroup, this.controlData.phoneNumberRegex, this.isMainPassenger)
    );
    const frequentFlyerCards = this.passengerFormGroup.controls[PaxFormFields.frequentFlyerCards];
    if (frequentFlyerCards) {
      this.frequentFlyerCardOptions$ = combineLatest([
        frequentFlyerCards.valueChanges.pipe(startWith(frequentFlyerCards.value as [])),
        this.frequentFlyerAirlines$,
      ]).pipe(
        map(([currentSelection, frequentFlyerAirlines]) => {
          if (isEmpty(currentSelection)) {
            return [frequentFlyerAirlines];
          }
          const selectedCodes = currentSelection.map((selected) => selected.companyCode);
          return currentSelection.map(({ companyCode }) =>
            frequentFlyerAirlines.filter(
              (airline) => !selectedCodes.includes(airline.value) || companyCode === airline.value
            )
          );
        }),
        distinctUntilChanged(),
        finShare()
      );
    }

    const frequentFlyerCard = this.passengerFormGroup.get(PaxFormFields.frequentFlyerCard);
    if (frequentFlyerCard) {
      this.frequentFlyerIsOpen$ = combineLatest([
        frequentFlyerCard.valueChanges.pipe(
          startWith({ companyCode: '', cardNumber: '' }), // to trigger valueChanges when already logged in
          map(({ companyCode, cardNumber }) => {
            return !isBlank(companyCode) || !isBlank(cardNumber);
          })
        ),
        this.frequentFlyerToggle$,
      ]).pipe(
        map(([ffHasData, toggle]) => {
          return ffHasData || toggle;
        })
      );
    }
  }

  private setupPhonePrefixes() {
    this.phonePrefixes$ = this.languageService.translate('countryCodes').pipe(
      map((countryCodes: TranslatedCountryCode[]) => {
        const locations = [this.controlData?.originCountryCode, this.controlData?.destinationCountryCode];
        const baseOptions = sortBy(countryCodes.map(countryCodeToOption), (option) => option.name);
        const optionsBasedOnLocations = locations
          .map((code) => countryCodeToOption(countryCodes.find(matchingCountryCode(code))))
          .filter((option) => !!option)
          .filter(uniqueBy((country) => country['value']));
        return optionsBasedOnLocations.length === 0
          ? baseOptions
          : [...optionsBasedOnLocations, SEPARATOR, ...baseOptions];
      })
    );
  }

  showContactDetailsFields(): void {
    this.showContactDetails = true;
    this.changeDetectorRef.detectChanges();

    this.elementRef.nativeElement.querySelector('[data-test-email] input')?.focus();
  }

  removeLeadingZero(event: Event): void {
    if (event.type !== 'input') {
      return;
    }

    const inputEvent = event as InputEvent;
    const inputField = event.target as HTMLInputElement;

    if (!inputEvent.inputType && inputField.value[0] === '0') {
      this.passengerFormGroup.get(this.FIELDS.phone).get(this.FIELDS.phoneNumber).setValue(inputField.value.slice(1));
    }
  }

  toggleFrequentFlyerSection(isOpen: boolean): void {
    const joinFinnairPlus = this.passengerFormGroup.get(PaxFormFields.joinFinnairPlus);
    this.frequentFlyerToggle$.next(isOpen);
    const frequentFlyer = this.passengerFormGroup.get(PaxFormFields.frequentFlyerCard);

    if (!isOpen) {
      frequentFlyer.get(PaxFormFields.cardNumber).setValue('');
      frequentFlyer.get(PaxFormFields.companyCode).setValue('');
    }
    if (isOpen && frequentFlyer.get(PaxFormFields.companyCode).value === '') {
      // Preselect Finnair
      frequentFlyer.get(PaxFormFields.companyCode).setValue('AY');
    }
    if (joinFinnairPlus.value === true) {
      joinFinnairPlus.setValue(false);
    }
  }

  toggleJoinFinnairPlus(): void {
    const joinFinnairPlus = this.passengerFormGroup.get(PaxFormFields.joinFinnairPlus);
    const frequentFlyer = this.passengerFormGroup.get(PaxFormFields.frequentFlyerCard);

    if (joinFinnairPlus.value) {
      this.frequentFlyerToggle$.next(false);
      frequentFlyer.get(PaxFormFields.cardNumber).setValue('');
      frequentFlyer.get(PaxFormFields.companyCode).setValue('');
    }
  }

  setupJoinFinnairPlus(): void {
    const frequentFlyer = this.passengerFormGroup.get(PaxFormFields.frequentFlyerCard);
    const joinFinnairPlus = this.passengerFormGroup.get(PaxFormFields.joinFinnairPlus);
    if (!frequentFlyer) {
      return;
    }
    // Set initial statuses
    if (
      isFrequentFlyerFieldFilled(
        frequentFlyer.get(PaxFormFields.cardNumber).value,
        frequentFlyer.get(PaxFormFields.companyCode).value
      )
    ) {
      this.frequentFlyerToggle$.next(true);
      joinFinnairPlus.setValue(false);
    } else if (joinFinnairPlus.value) {
      this.frequentFlyerToggle$.next(false);
      joinFinnairPlus.setValue(true);
    }
  }
}
